import React, { useState, useEffect } from 'react';
import MuiIconButton from '@material-ui/core/IconButton';
import MuiCircularProgress from '@material-ui/core/CircularProgress';
import MuiTableRow from '@material-ui/core/TableRow';
import MuiTableCell from '@material-ui/core/TableCell';
import MuiAddCircleIcon from '@material-ui/icons/AddCircle';
import { WithTooltip } from 'utils/wrappers';

import max from 'lodash/max';
import omit from 'lodash/omit';

import { useQuery } from 'react-query';
import ENUMS from '../../constants/ENUMS.gen.json';
import * as API from 'api';

import EditSplitRow from './edit-split-row';

const Groups = API.Groups();
const Parties = API.Parties();

function EditSplits({ splits, user_group_id, setIsInvalid, setSplitFormValue, colDefs }) {
  const [splitValues, setSplitValues] = useState(parseSplits(splits));
  const [totalPercentage, setTotalPercentage] = useState(getTotals(splits));
  const [totalError, setTotalError] = useState(false);

  //validates and tells parent about the changes.
  //If it was much deeper context could be used.
  function updateValues(idx, values) {
    setSplitValues(prevSplitValues => {
      return { ...prevSplitValues, [idx]: values };
    });
  }

  useEffect(() => {
    const total = getTotals(splitValues);
    const error = total === 100 ? false : 'Percentage must total 100%';
    setTotalError(error);
    setIsInvalid(error ? true : false);
    setTotalPercentage(total);
    setSplitFormValue(splitValues);
  }, [splitValues]);

  function addEmptySplit() {
    setSplitValues(prevSplitValues => {
      const nextIdx = 1 + max(Object.keys(prevSplitValues).map(Number));
      return { ...prevSplitValues, [nextIdx]: { roles: [], percentage: 0 } };
    });
  }

  function removeSplit(idx) {
    setSplitValues(prevSplitValues => omit(prevSplitValues, idx.toString()));
  }

  const { data: payees, isLoading: loadingPayees } = useQuery(
    ['payees', user_group_id],
    () => Groups.getPayees(user_group_id),
    { enabled: !!user_group_id }
  );

  const { data: contributors, isLoading: loadingContributors } = useQuery(['contributors', user_group_id], () =>
    Parties.getGroupPartiesByType(user_group_id, ENUMS.PARTY_TYPE.CONTRIBUTOR)
  );

  if (loadingContributors || loadingPayees) return <MuiCircularProgress />;

  return (
    <>
      {/* Using idx for split because there can be new items without an id */}
      {Object.entries(splitValues).map(([idx, split]) => (
        <EditSplitRow
          key={idx}
          idx={idx}
          split={split}
          updateValues={updateValues}
          payees={payees || []}
          contributors={contributors || []}
          removeSplit={removeSplit}
          colDefs={colDefs}
        />
      ))}
      <MuiTableRow>
        <MuiTableCell>
          <WithTooltip tooltip="Add Split">
            <MuiIconButton onClick={() => addEmptySplit()}>
              <MuiAddCircleIcon />
            </MuiIconButton>
          </WithTooltip>
        </MuiTableCell>
        <MuiTableCell></MuiTableCell>
        <MuiTableCell></MuiTableCell>
        <MuiTableCell>{totalError || ''}</MuiTableCell>
        <MuiTableCell>{totalPercentage}</MuiTableCell>
      </MuiTableRow>
    </>
  );
}

function getTotals(splitValues) {
  return Object.entries(splitValues).reduce((total, [_key, row]) => total + Number(row.percentage), 0);
}

//Sets data for the form
function parseSplits(splits) {
  function reduceSplits(all, split, idx) {
    const {
      id,
      asset_track_id,
      asset_party_id,
      percentage,
      roles,
      user_group_payee_id,
      payee: {
        payment_account: { wallet_address },
      },
    } = split;

    return {
      ...all,
      [idx]: {
        id,
        asset_track_id,
        asset_party_id,
        percentage,
        roles,
        user_group_payee_id,
        payee_payment_account_wallet_address: wallet_address,
      },
    };
  }
  return splits.reduce(reduceSplits, {});
}

export default EditSplits;
